import React, { useState, useRef, useCallback, useEffect, Suspense, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { optimizeImageUrl, getResponsiveImagePreset } from '../utils/ImageUtils';
import { useMovieDetails } from '../hooks/useMovieDetails';
import { getDotPlayerConfig } from '../config/AppConfig';
import ErrorBoundary from './common/ErrorBoundary';
import { useAuth0 } from '@auth0/auth0-react';
import spotlightService from '../services/SpotlightService';
import userDataService from '../services/UserDataService';
import MetaTags from './SEO/MetaTags';
import SocialSharing from './SocialSharing';
import { generateMovieSEO, generateStructuredData } from '../utils/SEOUtils';
import PlayerReactionButtons from './PlayerReactionButtons';

import './WatchTransition.css';

const MoviePDP = ({ movieId, onRelatedContentSelect, isWatchMode: propIsWatchMode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth0();

  // Add mount/unmount logging and cleanup
  useEffect(() => {
    return () => {
      // Clear caches and refresh resumptions data when leaving the page
      // Clear the resumptions cache to force fresh API calls
      spotlightService.clearResumptionsCache();

      // Clear video watch point cache to force fresh API calls
      spotlightService.clearVideoWatchPointCache();

      // Refresh the appload resumptions data
      userDataService.refreshResumptionsData().catch(error => {
        console.error('🔥 MoviePDP: Error refreshing resumptions data:', error);
      });
    };
  }, []);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showPreloader, setShowPreloader] = useState(false);
  const [playerLoaded, setPlayerLoaded] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showReactionButtons, setShowReactionButtons] = useState(false);
  const reactionButtonTimeoutRef = useRef(null);

  // Subscription/Paywall state
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);

  // Ad-gate state
  const [productData, setProductData] = useState(null);
  const [hasPaymentProfile, setHasPaymentProfile] = useState(false);
  const [showQuickPurchaseModal, setShowQuickPurchaseModal] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);

  // Watch point tracking state
  const [watchPointData, setWatchPointData] = useState(null);
  const saveIntervalRef = useRef(null);
  const lastSaveTimeRef = useRef(0);
  const videoElementRef = useRef(null);

  // Detect if we're in watch mode (either from prop or URL)
  const isWatchMode = propIsWatchMode || location.pathname.startsWith('/watch/');

  // Overlay controls state for watch mode
  const [showOverlay, setShowOverlay] = useState(true);
  const overlayTimeoutRef = useRef(null);
  const playerContainerRef = useRef(null);

  // Handle mouse movement and interaction
  useEffect(() => {
    if (!isWatchMode) return;

    const resetOverlayTimer = () => {
      if (overlayTimeoutRef.current) {
        clearTimeout(overlayTimeoutRef.current);
      }

      setShowOverlay(true);
      overlayTimeoutRef.current = setTimeout(() => {
        setShowOverlay(false);
      }, 3000); // Hide after 3 seconds of inactivity
    };

    const handleUserActivity = () => {
      resetOverlayTimer();
    };

    // Add event listeners
    document.addEventListener('mousemove', handleUserActivity);
    document.addEventListener('mousedown', handleUserActivity);
    document.addEventListener('keydown', handleUserActivity);
    document.addEventListener('touchstart', handleUserActivity);

    // Initial timer
    resetOverlayTimer();

    return () => {
      document.removeEventListener('mousemove', handleUserActivity);
      document.removeEventListener('mousedown', handleUserActivity);
      document.removeEventListener('keydown', handleUserActivity);
      document.removeEventListener('touchstart', handleUserActivity);
      if (overlayTimeoutRef.current) {
        clearTimeout(overlayTimeoutRef.current);
      }
    };
  }, [isWatchMode]); // Only depend on isWatchMode

  // Use the new custom hook for movie data fetching
  const { movie, relatedContent, loading, error, retry } = useMovieDetails(movieId);

  // Check subscription access when movie data loads
  useEffect(() => {
    const checkSubscription = async () => {
      if (!movie || !movie._id) return;

      // If content has ads_enabled but purchasable is NOT explicitly true, we MUST call API
      const hasAdsButPurchasableNotConfirmed = movie.subscription_access?.ads_enabled === true && 
        movie.subscription_access?.purchasable !== true;

      // First check if this channel has a paywall (is_product flag)
      // Only skip API check if is_product is explicitly false AND content is not ad-gated
      if (movie.is_product === false && !hasAdsButPurchasableNotConfirmed) {
        setSubscriptionData({ unlocked: true, ads_enabled: false, purchasable: false });
        return;
      }

      // Channel has paywall or is_product is undefined, or has ads with unknown purchasable - check API
      setIsCheckingSubscription(true);
      try {
        const subscriptionAccess = await spotlightService.checkSubscriptionAccess(movie.dspro_id || movie._id);
        setSubscriptionData(subscriptionAccess);
      } catch (error) {
        console.error('💳 MoviePDP: Failed to check subscription:', error);
        // Set default locked state on error
        setSubscriptionData({
          success: false,
          unlocked: false,
          ads_enabled: true
        });
      } finally {
        setIsCheckingSubscription(false);
      }
    };

    checkSubscription();
  }, [movie?._id]);

  // Check if we should show the "Go Ad-Free" button (ad-gate scenario on watch page)
  const shouldShowGoAdFree = useMemo(() => {
    if (!isWatchMode) return false; // Only show on watch page
    const { unlocked, ads_enabled, purchasable } = subscriptionData || {};
    return unlocked === true && ads_enabled === true && purchasable === true;
  }, [isWatchMode, subscriptionData]);

  // Check if content is TVOD locked (must purchase to watch)
  const isTVODLocked = useMemo(() => {
    const { unlocked, purchasable } = subscriptionData || {};
    return unlocked === false && purchasable === true;
  }, [subscriptionData]);

  // Check if content is unavailable (locked but not purchasable)
  const isContentUnavailable = useMemo(() => {
    const { unlocked, purchasable } = subscriptionData || {};
    return unlocked === false && purchasable !== true;
  }, [subscriptionData]);

  // Fetch product data when content is purchasable (for both ad-gated and TVOD locked)
  useEffect(() => {
    // Fetch product data if:
    // 1. Content is ad-gated (unlocked but has ads and purchasable) OR
    // 2. Content is TVOD locked (not unlocked and purchasable)
    const shouldFetchProduct = subscriptionData?.purchasable === true && (
      subscriptionData?.ads_enabled === true || // Ad-gated
      subscriptionData?.unlocked === false // TVOD locked
    );
    
    if (!shouldFetchProduct) return;
    
    const fetchProductData = async () => {
      const productId = subscriptionData?.dspro_id || movie?.dspro_id || movie?._id;
      if (!productId) return;

      try {
        const products = await spotlightService.getProducts();
        const product = products?.find(p => p._id === productId || p.channel_id === productId);
        if (product) {
          setProductData(product);
        }
        
        // Check if user has a payment profile
        if (isAuthenticated) {
          const hasProfile = await spotlightService.hasPaymentProfile();
          setHasPaymentProfile(hasProfile);
        }
      } catch (error) {
        console.error('MoviePDP: Error fetching product data:', error);
      }
    };

    fetchProductData();
  }, [subscriptionData, movie?.dspro_id, movie?._id, isAuthenticated]);

  // Handle "Go Ad-Free" purchase flow
  const handleGoAdFree = useCallback(() => {
    const productId = subscriptionData?.dspro_id || movie?.dspro_id || movie?._id;
    
    if (!productId) {
      console.error('MoviePDP: No product ID available for purchase');
      return;
    }

    // If user has a payment profile, show quick purchase modal
    if (hasPaymentProfile) {
      setShowQuickPurchaseModal(true);
    } else {
      // Navigate to checkout page
      navigate(`/checkout/${productId}`, { 
        state: { channel: movie, returnTo: window.location.pathname }
      });
    }
  }, [subscriptionData, movie, hasPaymentProfile, navigate]);

  // Handle quick purchase confirmation
  const handleQuickPurchaseConfirm = useCallback(async () => {
    setIsPurchasing(true);
    setPurchaseError(null);

    const productId = subscriptionData?.dspro_id || movie?.dspro_id || movie?._id;

    try {
      console.log('💳 MoviePDP: Processing quick purchase for:', productId);
      const result = await spotlightService.purchaseProduct(productId);
      
      if (result.success) {
        console.log('✅ MoviePDP: Quick purchase successful');
        
        // CRITICAL: Refresh the client token after purchase to get updated entitlements
        // The old token doesn't know about the new purchase, so API calls would return stale data
        console.log('🔄 MoviePDP: Refreshing client token after purchase...');
        try {
          await spotlightService.refreshClientToken();
          console.log('✅ MoviePDP: Client token refreshed successfully');
        } catch (tokenError) {
          console.warn('⚠️ MoviePDP: Could not refresh client token:', tokenError);
          // Continue anyway - page reload will force re-auth
        }
        
        setShowQuickPurchaseModal(false);
        
        // Clear ALL subscription caches to ensure fresh data
        spotlightService.clearSubscriptionCaches();
        
        // Reload the page to get fresh subscription data with new token
        window.location.reload();
      } else {
        setPurchaseError(result.message || 'Purchase failed. Please try again.');
      }
    } catch (error) {
      console.error('❌ MoviePDP: Purchase error:', error);
      setPurchaseError(error.message || 'An error occurred. Please try again.');
    } finally {
      setIsPurchasing(false);
    }
  }, [subscriptionData, movie]);

  const playerRef = useRef(null);

  // Watch point functionality for watch mode
  useEffect(() => {
    if (!isWatchMode || !isAuthenticated || !user || !movie?.video?._id) return;

    const videoId = movie.video._id;
    const channelId = movie._id;
    const parentChannelId = movie.parent_channel_id || movie.parentChannelId;

    // Load existing watch point
    const loadWatchPoint = async () => {
      try {
        // Check if this video is in watch-again (completed) vs continue-watching
        const watchAgain = userDataService.getWatchAgain();
        const continueWatching = userDataService.getContinueWatching();
        
        // Find if this video is in watch-again
        const watchAgainMatch = watchAgain?.find(item => 
          item.video_id === videoId || 
          item.channel?.video?._id === videoId ||
          item.channel?._id === channelId ||
          item._id === videoId
        );
        
        // Find if this video is in continue-watching
        const continueWatchingMatch = continueWatching?.find(item => 
          item.video_id === videoId || 
          item.channel?.video?._id === videoId ||
          item.channel?._id === channelId ||
          item._id === videoId
        );

        // If it's in watch-again and not in continue-watching, it's completed - start from beginning
        if (watchAgainMatch && !continueWatchingMatch) {
          setWatchPointData(null); // No watch point = start from beginning
          return;
        }

        // First, try to get watch point from continue watching data
        if (continueWatchingMatch && continueWatchingMatch.point > 0) {
          // Create a mock response structure to match what the API would return
          const mockWatchPoint = {
            success: true,
            data: {
              point: continueWatchingMatch.point,
              video_id: videoId,
            },
          };

          setWatchPointData(mockWatchPoint);
          return;
        }

        // Fallback: call API if not found in continue watching data
        const watchPoint = await spotlightService.getVideoWatchPoint(videoId);

        // Handle inconsistent server response structure
        // Server sometimes returns {success: true, data: {point: 346}}
        // and sometimes returns {success: true, result: {_id: '...', video_id: '...'}}
        let watchPointValue = null;

        if (watchPoint?.data?.point > 0) {
          // First response structure: {success: true, data: {point: 346}}
          watchPointValue = watchPoint.data.point;
        } else if (watchPoint?.result?.point > 0) {
          // Second response structure: {success: true, result: {point: 346}}
          watchPointValue = watchPoint.result.point;
        } else {
        }

        if (watchPointValue) {
          setWatchPointData(watchPoint);
        } else {
          setWatchPointData(null);
        }
      } catch (error) {
        console.error('🎬 MoviePDP: Error loading watch point:', error);
        setWatchPointData(null);
      }
    };

    loadWatchPoint();

    // Set up periodic saving
    const startPeriodicSaving = () => {
      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current);
      }

      saveIntervalRef.current = setInterval(async () => {
        if (!videoElementRef.current || !isAuthenticated || !user) return;

        const currentTime = videoElementRef.current.currentTime;
        const duration = videoElementRef.current.duration;

        // Only save if we have valid time data and enough time has passed since last save
        if (currentTime > 0 && duration > 0 && Date.now() - lastSaveTimeRef.current > 4000) {
          try {
            await spotlightService.saveVideoWatchPoint(videoId, currentTime, {
              duration: duration,
              channelId: channelId,
              parentChannelId: parentChannelId,
            });

            lastSaveTimeRef.current = Date.now();
          } catch (error) {
            console.error('🎬 MoviePDP: Error saving watch point:', error);
          }
        }
      }, 5000); // Save every 5 seconds
    };

    // Set up video element event listeners
    const setupVideoListeners = () => {
      const videoElement = document.querySelector('#pdp-player video');
      if (!videoElement) {
        setTimeout(setupVideoListeners, 1000);
        return;
      }

      videoElementRef.current = videoElement;

      // Resume from watch point if available - handle both response structures
      // Handle both response structures: data.point and result.point
      const watchPointValue = watchPointData?.data?.point || watchPointData?.result?.point;

      if (watchPointData && watchPointValue > 0) {
        const resumeTime = Math.min(watchPointValue, videoElement.duration - 10);
        if (resumeTime > 0) {
          videoElement.currentTime = resumeTime;
        }
      }

      // Start periodic saving immediately since video autoplays
      startPeriodicSaving();

      // Start periodic saving when video starts playing (for manual play)
      const handlePlay = () => {
        console.log('🎬 Video playing - hiding reaction buttons');
        setIsPaused(false);
        startPeriodicSaving();
        
        // Clear any pending timeout and immediately hide reaction buttons
        if (reactionButtonTimeoutRef.current) {
          clearTimeout(reactionButtonTimeoutRef.current);
          reactionButtonTimeoutRef.current = null;
        }
        setShowReactionButtons(false);
      };

      const handlePause = () => {
        console.log('⏸️ Video paused - showing reaction buttons with delay');
        setIsPaused(true);
        
        // Save immediately on pause
        if (videoElement.currentTime > 0 && videoElement.duration > 0) {
          spotlightService
            .saveVideoWatchPoint(videoId, videoElement.currentTime, {
              duration: videoElement.duration,
              channelId: channelId,
              parentChannelId: parentChannelId,
            })
            .catch(error => {
              console.error('🎬 MoviePDP: Error saving on pause:', error);
            });
        }
        
        // Clear any existing timeout
        if (reactionButtonTimeoutRef.current) {
          clearTimeout(reactionButtonTimeoutRef.current);
        }
        
        // Only show reaction buttons after a brief delay to avoid flashing during seeking
        reactionButtonTimeoutRef.current = setTimeout(() => {
          // Double-check that we're still paused before showing buttons
          if (videoElement && videoElement.paused) {
            console.log('✅ Movie: Showing reaction buttons after pause delay');
            setShowReactionButtons(true);
          }
        }, 300); // 300ms delay to avoid flashing during seeking
      };

      const handleEnded = () => {
        // Clear watch point when video ends
        spotlightService
          .saveVideoWatchPoint(videoId, 0, {
            duration: videoElement.duration,
            channelId: channelId,
            parentChannelId: parentChannelId,
          })
          .catch(error => {
            console.error('🎬 MoviePDP: Error clearing watch point on end:', error);
          });
      };

      console.log('🔧 Setting up video event listeners on element:', videoElement);
      videoElement.addEventListener('play', handlePlay);
      videoElement.addEventListener('pause', handlePause);
      videoElement.addEventListener('ended', handleEnded);
      console.log('✅ Video event listeners attached successfully');

      return () => {
        videoElement.removeEventListener('play', handlePlay);
        videoElement.removeEventListener('pause', handlePause);
        videoElement.removeEventListener('ended', handleEnded);
        
        // Clear reaction button timeout
        if (reactionButtonTimeoutRef.current) {
          clearTimeout(reactionButtonTimeoutRef.current);
          reactionButtonTimeoutRef.current = null;
        }
      };
    };

  // Set up video listeners when player is loaded
  console.log('🔍 Video listener effect - playerLoaded:', playerLoaded, 'isWatchMode:', isWatchMode, 'isAuthenticated:', isAuthenticated);
  if (playerLoaded) {
    console.log('✅ Player loaded - setting up video listeners');
    const cleanup = setupVideoListeners();
    return cleanup;
  } else {
    console.log('⏳ Player not loaded yet, waiting...');
  }

    return () => {
      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current);
      }
    };
  }, [isWatchMode, isAuthenticated, user, movie, playerLoaded]); // 🚀 FIXED: Removed watchPointData from dependencies

  // Auto-play when in watch mode - wait for movie data to load
  // IMPORTANT: Do NOT auto-play if content is TVOD locked
  useEffect(() => {
    // Don't auto-play if content is TVOD locked or unavailable
    if (isTVODLocked || isContentUnavailable) {
      console.log('🔒 MoviePDP: Content is locked, not auto-playing');
      return;
    }

    // Only proceed if we're in watch mode, have movie data, and not already playing
    if (isWatchMode && movie && movie.video?._id && !isPlaying && !loading) {
      setIsPlaying(true);
    } else if (isWatchMode && !movie && !loading) {
      console.warn('⚠️ MoviePDP: Watch mode active but no movie data after loading completed');
    }
  }, [isWatchMode, movie, isPlaying, loading, isTVODLocked, isContentUnavailable]); // Include lock states

  // Stable player mounting effect - Following DPlayer Pro documentation
  useEffect(() => {
    if (!isPlaying) return;
    
    // Wait for subscription check to complete before mounting player
    // This ensures we have the correct ads_enabled state from the API
    // Only wait if a check is actively in progress
    if (isCheckingSubscription) {
      console.log('🎬 MoviePDP: Subscription check in progress, delaying player mount...');
      return;
    }

    let mounted = true;

    const mountPlayer = async () => {
      // Wait for DOM and DotPlayer to be ready
      await new Promise(resolve => setTimeout(resolve, 200));

      if (!mounted || !window.DotPlayer || !playerRef.current) return;

      try {
        // Extract video ID from movie data - use movie.video._id (the correct field!)
        const videoId = movie?.video?._id;

        if (!videoId) {
          console.warn('⚠️ MoviePDP: No video ID available for player mounting');
          return;
        }

        // IMPORTANT: Unmount any existing DotPlayer instance first
        if (window.DotPlayer.el) {
          try {
            window.DotPlayer.unmount();
            // Wait a moment for cleanup
            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (error) {
            // No existing player to unmount (this is fine)
          }
        }

        // Get DotPlayer configuration from centralized config
        // Pass ads_enabled from subscription_access to control VMAP loading
        const adsEnabled = subscriptionData?.ads_enabled !== false;
        console.log('🎬 MoviePDP: Player mounting with subscription data:', {
          ads_enabled: subscriptionData?.ads_enabled,
          adsEnabled,
          unlocked: subscriptionData?.unlocked,
          purchasable: subscriptionData?.purchasable,
        });
        const playerConfig = await getDotPlayerConfig(videoId, '#pdp-player', {
          autostart: true,
          controls: true,
          muted: false,
          playsInline: true,
          fluid: true,
          aspectRatio: '16:9',
          controls: true,
          skin: true,
          pip: false,
          theme: {
            fontColor: '#f2f2f2', // main icon color
            fontColorHover: '#31B37C', // icon color when hovered over
            progressSliderMain: '#31B37C', // progress slider color
            progressSliderBackground: '#B4B7BB', // progress slider background color
            controlBar: '#000000', // control bar gradient background color
          },
        }, { adsEnabled });

        // Mount player following DPlayer Pro documentation
        const player = await window.DotPlayer.mount(playerConfig);

        if (mounted) {
          // Wait for player to be fully loaded as per documentation
          console.log('🔄 Waiting for DotPlayer to load...');
          await player.isPlayerLoaded();
          console.log('✅ DotPlayer loaded successfully');
          setPlayerLoaded(true);
          console.log('✅ playerLoaded state set to true');
          
          setTimeout(() => {
            if (mounted) setShowPreloader(false);
          }, 300);
        }
      } catch (error) {
        console.error('❌ MoviePDP: Player mount error:', error);
        if (mounted) setShowPreloader(false);
      }
    };

    mountPlayer();

    return () => {
      mounted = false;
      if (window.DotPlayer) {
        try {
          // Clean up pause listeners if they exist
          if (window.DotPlayer.el?.cleanupPauseListeners) {
            window.DotPlayer.el.cleanupPauseListeners();
          }
          window.DotPlayer.unmount();
        } catch (error) {
          console.warn('Unmount error (ignored):', error);
        }
      }
      setPlayerLoaded(false);
      setIsPaused(false);
      setShowReactionButtons(false);
      
      // Clear reaction button timeout
      if (reactionButtonTimeoutRef.current) {
        clearTimeout(reactionButtonTimeoutRef.current);
        reactionButtonTimeoutRef.current = null;
      }
    };
  }, [isPlaying, movie, subscriptionData, isCheckingSubscription]); // Include subscription data to ensure correct ad state

  // Memoized event handlers
  const handlePlayClick = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handleBackClick = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleAddToWatchlist = useCallback(() => {
    // This would be implemented with actual watchlist functionality
    // Added to watchlist
    // Show a toast or notification
    alert(`Added "${movie?.title}" to your watchlist`);
  }, [movie?.title]);

  // Hero image with optimization
  const getHeroImage = useCallback(() => {
    if (!movie) return null;

    const heroImageUrl =
      movie.spotlight_poster ||
      movie.poster ||
      movie.theme?.image?.spotlight_poster ||
      movie.theme?.image?.poster;

    if (!heroImageUrl) return null;

    return optimizeImageUrl(heroImageUrl, getResponsiveImagePreset('CHANNEL_HERO'));
  }, [movie]);

  // Navigation buttons component for reuse across states
  const NavigationButtons = ({ isOverlay = false }) => (
    <div className={`flex items-center space-x-4 ${isOverlay ? 'mb-0' : 'mb-4'}`}>
      <button
        onClick={handleBackClick}
        className="flex items-center text-white hover:text-[#56bdaf] transition-colors"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-5 h-5 mr-1"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
            clipRule="evenodd"
          />
        </svg>
        Back
      </button>
      {!isWatchMode && (
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-white hover:text-[#56bdaf] transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-5 h-5 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
          </svg>
          Home
        </button>
      )}
    </div>
  );

  const logo = `${
    window.ASSETS_BASE_URL || window.location.origin
  }/assets/images/chillfree-logo.png`;
  // Netflix-style preloader component with actual logo
  const Preloader = () => (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black">
      <div className="text-center">
        {/* Actual Chillfree Logo with growing animation */}
        <div className="mb-8">
          <img src={logo} alt="ChillFree" className="h-20 md:h-24 mx-auto preloader-logo" />
        </div>

        {/* Enhanced Progress Spinner */}
        <div className="relative w-20 h-20 mx-auto mb-6">
          {/* Outer ring */}
          <div className="absolute inset-0 border-4 border-gray-800 rounded-full"></div>
          {/* Animated ring */}
          <div className="absolute inset-0 border-4 border-[#56bdaf] border-t-transparent rounded-full animate-spin"></div>
          {/* Inner glow effect */}
          <div
            className="absolute inset-2 border-2 border-[#56bdaf]/30 border-t-transparent rounded-full animate-spin"
            style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}
          ></div>
        </div>

        {/* Loading Text with pulse */}
        <p className="text-lg text-white/80 animate-pulse">Loading your movie experience...</p>

        {/* Progress dots */}
        <div className="flex justify-center mt-4 space-x-2">
          <div
            className="w-2 h-2 bg-[#56bdaf] rounded-full animate-bounce"
            style={{ animationDelay: '0ms' }}
          ></div>
          <div
            className="w-2 h-2 bg-[#56bdaf] rounded-full animate-bounce"
            style={{ animationDelay: '150ms' }}
          ></div>
          <div
            className="w-2 h-2 bg-[#56bdaf] rounded-full animate-bounce"
            style={{ animationDelay: '300ms' }}
          ></div>
        </div>
      </div>
    </div>
  );

  // Watch point status indicator
  const WatchPointIndicator = () => {
    // Silent implementation - no visual indicators
    return null;
  };

  // Paywall Overlay for TVOD locked content
  const PaywallOverlay = () => {
    const productId = movie?.dspro_id || movie?._id;
    const priceText = productData?.price ? ` for ${productData.price}` : '';
    
    return (
      <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/95">
        <div className="text-center max-w-lg mx-auto p-8">
          {/* Lock Icon */}
          <div className="mb-6">
            <div className="w-24 h-24 mx-auto bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center shadow-lg">
              <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          
          {/* Title */}
          <h2 className="text-3xl font-bold text-white mb-3">Purchase Required</h2>
          
          {/* Movie Title */}
          <p className="text-xl text-[#56bdaf] font-semibold mb-4">{movie?.title}</p>
          
          {/* Description */}
          <p className="text-gray-400 mb-8">
            This content requires purchase to watch. Buy now{priceText} to start watching instantly.
          </p>
          
          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate(`/checkout/${productId}`, { state: { channel: movie, returnTo: window.location.pathname } })}
              className="px-8 py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-white font-bold rounded-full text-lg shadow-lg transition-all flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              Buy Now{priceText}
            </button>
            <button
              onClick={handleBackClick}
              className="px-8 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-full text-lg transition-all border border-white/20"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Unavailable Content Overlay (locked but not purchasable)
  const UnavailableOverlay = () => (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/95">
      <div className="text-center max-w-lg mx-auto p-8">
        {/* Lock Icon */}
        <div className="mb-6">
          <div className="w-24 h-24 mx-auto bg-gradient-to-br from-gray-500 to-gray-600 rounded-full flex items-center justify-center shadow-lg">
            <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
        
        {/* Title */}
        <h2 className="text-3xl font-bold text-white mb-3">Content Unavailable</h2>
        
        {/* Movie Title */}
        <p className="text-xl text-gray-400 font-semibold mb-4">{movie?.title}</p>
        
        {/* Description */}
        <p className="text-gray-400 mb-8">
          This content is currently not available for viewing.
        </p>
        
        {/* Action Button */}
        <button
          onClick={handleBackClick}
          className="px-8 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-full text-lg transition-all border border-white/20"
        >
          Go Back
        </button>
      </div>
    </div>
  );

  // Overlay controls for watch mode
  const OverlayControls = () => (
    <div
      className={`absolute top-0 left-0 right-0 bottom-0 z-50 transition-opacity duration-300 pointer-events-none ${
        showOverlay ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {/* Top Bar with Navigation and Movie Info */}
      <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 via-black/40 to-transparent p-6 pointer-events-auto">
        <NavigationButtons isOverlay={true} />
        {movie && (
          <div className="mt-4">
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{movie.title}</h1>
            <div className="flex flex-wrap items-center text-white/80 text-sm space-x-4">
              <span>{movie.year || movie.video?.year || 'Unknown year'}</span>
              <span>•</span>
              <span>{movie.genres?.join(', ') || 'Unknown genre'}</span>
              {movie.rating && (
                <>
                  <span>•</span>
                  <span className="bg-[#56bdaf]/20 px-2 py-0.5 rounded text-[#56bdaf]">
                    {movie.rating}
                  </span>
                </>
              )}
            </div>
            {/* Go Ad-Free Button - for ad-gated content */}
            {shouldShowGoAdFree && (
              <button 
                className="mt-3 px-4 py-2 bg-gradient-to-r from-[#31B37C] to-[#27a06e] text-white text-sm font-semibold rounded flex items-center gap-2 hover:from-[#3bc98e] hover:to-[#31B37C] transition-all"
                onClick={handleGoAdFree}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                </svg>
                Go Ad-Free
              </button>
            )}
          </div>
        )}
      </div>

        {/* Center Reaction Buttons - Only show when video is paused (with debounce) */}
        {console.log('🔍 Reaction button render check:', { showReactionButtons, isAuthenticated, movieId: movie?._id })}
        {showReactionButtons && isAuthenticated && movie?._id && (
          <>
            {console.log('✅ Rendering PlayerReactionButtons')}
            <PlayerReactionButtons 
              channelId={movie?.dspro_id || movie?._id}
              initialReaction={movie?.user_reaction || movie?.reaction || null}
              onReactionChange={(reaction) => {
                // Optional: Handle reaction change if needed
                console.log('Movie reaction changed:', reaction);
              }}
            />
          </>
        )}
    </div>
  );

  if (loading) {
    // In watch mode, let DotPlayer handle loading state
    if (isWatchMode) {
      return (
        <div className="watch-mode" style={{ width: '100vw', height: '100vh' }}>
          <div className="relative w-full h-full">
            {/* Let DotPlayer handle its own loading state */}
          </div>
        </div>
      );
    }

    // Regular PDP skeleton loading state
    return (
      <div className="min-h-screen movie-pdp bg-primary">
        <div className="container px-4 py-8 mx-auto">
          <NavigationButtons />

          {/* Skeleton loading state */}
          <div className="animate-pulse">
            <div className="w-full mb-8 bg-gray-700 rounded-lg h-96"></div>
            <div className="w-3/4 h-10 mb-4 bg-gray-700 rounded"></div>
            <div className="w-1/4 h-4 mb-6 bg-gray-700 rounded"></div>
            <div className="w-full h-4 mb-2 bg-gray-700 rounded"></div>
            <div className="w-full h-4 mb-2 bg-gray-700 rounded"></div>
            <div className="w-5/6 h-4 mb-8 bg-gray-700 rounded"></div>

            <div className="flex mb-8 space-x-4">
              <div className="w-32 h-10 bg-gray-700 rounded"></div>
              <div className="w-32 h-10 bg-gray-700 rounded"></div>
            </div>

            <div className="w-48 h-6 mb-4 bg-gray-700 rounded"></div>
            <div className="flex space-x-4 overflow-x-auto">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="flex-shrink-0 w-64">
                  <div className="mb-2 bg-gray-700 rounded-lg h-36"></div>
                  <div className="w-3/4 h-4 mb-1 bg-gray-700 rounded"></div>
                  <div className="w-1/2 h-3 bg-gray-700 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen movie-pdp bg-primary">
        <div className="container px-4 py-8 mx-auto">
          <NavigationButtons />

          <div className="p-6 text-center border border-[#56bdaf] rounded-lg bg-[#56bdaf]/10">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-12 h-12 mx-auto mb-4 text-[#56bdaf]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h2 className="mb-2 text-xl font-bold text-white">Error Loading Movie</h2>
            <p className="mb-4 text-white/70">{error}</p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={handleBackClick}
                className="px-6 py-2 font-medium text-white rounded-full bg-[#56bdaf] hover:bg-[#4aa89c]"
              >
                Go Back
              </button>
              <button
                onClick={retry}
                className="px-6 py-2 font-medium text-white rounded-full bg-secondary hover:bg-[#2a2a2a]"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!movie) {
    return (
      <div className="min-h-screen movie-pdp bg-primary">
        <div className="container px-4 py-8 mx-auto">
          <NavigationButtons />

          <div className="py-12 text-center">
            <h2 className="mb-2 text-xl font-bold text-white">Movie Not Found</h2>
            <p className="mb-4 text-white/70">The requested movie could not be found.</p>
            <button
              onClick={handleBackClick}
              className="px-6 py-2 font-medium text-white rounded-full bg-[#56bdaf] hover:bg-[#4aa89c]"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const heroImage = getHeroImage();
  const title = movie.title || 'Untitled Movie';
  const description = movie.description || 'No description available';
  const initialDuration = Math.ceil(Number(movie.video?.duration));
  let duration = 'Unknown Duration';
  if (initialDuration) {
    const h = Math.floor(initialDuration / 3600);
    const m = Math.floor((initialDuration % 3600) / 60);
    const s = initialDuration % 60;
    if (h) {
      duration = `${h}h ${m}m ${s}s`;
    } else {
      duration = `${m}m ${s}s`;
    }
  }
  const releaseYear = movie.year || movie.video?.year || 'Unknown year';
  const genres = movie.genres?.join(', ') || 'Unknown genre';
  const rating = movie.rating || movie.video?.rating || 'Not rated';
  const actors = movie.actors || movie.video?.actors || [];
  const directors = movie.directors || movie.video?.directors || [];
  const writers = movie.writers || movie.video?.writers || [];

  // Watch mode layout - Simplified structure with restored functionality
  if (isWatchMode) {
    // Show paywall overlay for TVOD locked content
    if (isTVODLocked) {
      return (
        <div className="watch-container relative min-h-screen w-full bg-black" ref={playerContainerRef}>
          {/* Background image */}
          {heroImage && (
            <div 
              className="absolute inset-0 bg-cover bg-center opacity-20"
              style={{ backgroundImage: `url(${heroImage})` }}
            />
          )}
          {/* Paywall Overlay */}
          <PaywallOverlay />
        </div>
      );
    }

    // Show unavailable overlay for locked non-purchasable content
    if (isContentUnavailable && subscriptionData !== null) {
      return (
        <div className="watch-container relative min-h-screen w-full bg-black" ref={playerContainerRef}>
          {/* Background image */}
          {heroImage && (
            <div 
              className="absolute inset-0 bg-cover bg-center opacity-20"
              style={{ backgroundImage: `url(${heroImage})` }}
            />
          )}
          {/* Unavailable Overlay */}
          <UnavailableOverlay />
        </div>
      );
    }

    return (
      <div className="watch-container" ref={playerContainerRef}>
        {/* Preloader */}
        {showPreloader && <Preloader />}

        {/* Video Player */}
        <div id="pdp-player" ref={playerRef} />

        {/* Watch Point Status Indicator */}
        <WatchPointIndicator />

        {/* Overlay Controls */}
        {!showPreloader && <OverlayControls />}
      </div>
    );
  }

  // Regular PDP layout
  const seoData = generateMovieSEO(movie, channelData);
  const structuredData = generateStructuredData(movie, 'movie');

  return (
    <div className={`min-h-screen movie-pdp bg-primary ${isWatchMode ? 'watch-mode' : ''}`}>
      {/* SEO Meta Tags */}
      <MetaTags {...seoData} />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <div className="container px-4 py-8 mx-auto">
        <NavigationButtons />

        {isPlaying ? (
          <div className={`mb-8 ${isWatchMode ? 'watch-mode-player' : ''}`}>
            <div
              id="pdp-player"
              ref={playerRef}
              className={`w-full bg-black rounded-lg overflow-hidden ${
                isWatchMode ? 'aspect-video min-h-[80vh]' : 'aspect-video min-h-[400px]'
              }`}
            />
          </div>
        ) : (
          <div className="relative w-full h-[40vh] md:h-[60vh] rounded-b-2xl overflow-hidden mb-8">
            <img
              src={`${heroImage}?w=1200&h=675&fit=crop`}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/90 via-[#1a1a1a]/60 to-transparent" />
            <div className="relative z-10 flex flex-col justify-end h-full p-6">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">{title}</h1>
              <div className="flex flex-wrap items-center text-white/80 text-base mb-4 space-x-4">
                <span>{releaseYear}</span>
                <span>•</span>
                <span>{duration}</span>
                <span>•</span>
                <span>{genres}</span>
                {rating && (
                  <>
                    <span>•</span>
                    <span className="bg-[#56bdaf]/20 px-2 py-0.5 rounded text-[#56bdaf]">
                      {rating}
                    </span>
                  </>
                )}
              </div>
              <button
                onClick={subscriptionData?.unlocked === false ? () => navigate(`/checkout/${movie.dspro_id || movie._id}`, { state: { channel: movie } }) : handlePlayClick}
                className={`${
                  subscriptionData?.unlocked === false
                    ? 'bg-yellow-500 hover:bg-yellow-600'
                    : 'bg-[#56bdaf] hover:bg-[#4aa89c]'
                } text-white font-bold py-3 px-8 rounded-full text-lg shadow-lg w-max`}
              >
                {subscriptionData?.unlocked === false ? (
                  <>
                    <svg className="inline-block w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    Buy Now
                  </>
                ) : (
                  <>
                    <svg className="inline-block w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M6 4l10 6-10 6V4z" />
                    </svg>
                    {isCheckingSubscription ? 'Checking...' : 'Play'}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Content sections - hidden in watch mode but data is preserved */}
        {!isWatchMode && (
          <>
            <div className="flex flex-wrap items-center text-white/80 text-base mb-4 space-x-4">
              <span>{releaseYear}</span>
              <span>•</span>
              <span>{duration}</span>
              <span>•</span>
              <span>{genres}</span>
              <span>•</span>
              <span>Audio: English</span>
              <span>•</span>
              <span>Subtitles: English</span>
            </div>
            <div className="font-bold text-white mb-2">{description}</div>

            {/* Social Sharing */}
            <div className="mb-6">
              <SocialSharing
                channelSlug={movie?.slug}
                title={title}
                description={description}
                image={heroImage || movie?.spotlight_poster || movie?.poster}
              />
            </div>

            <ErrorBoundary fallback="Failed to load cast and crew information">
              <Suspense fallback={<LoadingSpinner message="Loading cast and crew..." />}>
                <CastCrew
                  actors={actors}
                  directors={directors}
                  writers={writers}
                  className="mb-8"
                />
              </Suspense>
            </ErrorBoundary>

            <div className="mb-8">
              <ErrorBoundary fallback="Failed to load related content">
                <Suspense fallback={<LoadingSpinner message="Loading related content..." />}>
                  {relatedContent.length > 0 && (
                    <RelatedContent items={relatedContent} onSelect={onRelatedContentSelect} />
                  )}
                </Suspense>
              </ErrorBoundary>
            </div>
          </>
        )}

        {/* Quick Purchase Modal for ad-gate */}
        {showQuickPurchaseModal && (
          <div 
            className="fixed inset-0 bg-black/85 flex items-center justify-center z-[10002]"
            onClick={() => setShowQuickPurchaseModal(false)}
          >
            <div 
              className="bg-gradient-to-b from-[#2a2a2a] to-[#1a1a1a] rounded-xl w-full max-w-md p-6 shadow-2xl border border-white/10"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-xl font-semibold text-white">Go Ad-Free</h3>
                <button 
                  className="bg-white/10 hover:bg-white/20 rounded-full w-8 h-8 flex items-center justify-center text-white transition-colors"
                  onClick={() => setShowQuickPurchaseModal(false)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="mb-6">
                <p className="text-gray-400 text-sm mb-3">
                  Purchase <strong className="text-white">{movie?.title}</strong> to remove ads and enjoy uninterrupted viewing.
                </p>
                {productData?.price && (
                  <p className="text-3xl font-bold text-[#31B37C] mt-4">{productData.price}</p>
                )}
                {purchaseError && (
                  <p className="text-[#ff6b6b] text-sm bg-[#ff6b6b]/10 px-3 py-2 rounded-md mt-3">{purchaseError}</p>
                )}
              </div>
              <div className="flex gap-3">
                <button 
                  className="flex-1 bg-white/10 border border-white/20 rounded-md py-3 px-4 text-white text-sm hover:bg-white/15 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setShowQuickPurchaseModal(false)}
                  disabled={isPurchasing}
                >
                  Cancel
                </button>
                <button 
                  className="flex-1 bg-gradient-to-r from-[#31B37C] to-[#27a06e] rounded-md py-3 px-4 text-white text-sm font-semibold hover:from-[#3bc98e] hover:to-[#31B37C] transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                  onClick={handleQuickPurchaseConfirm}
                  disabled={isPurchasing}
                >
                  {isPurchasing ? 'Processing...' : 'Confirm Purchase'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MoviePDP;
